import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, RefreshCw, ImagePlus, Loader2, CheckCircle2, XCircle, ScanLine, ShieldCheck } from "lucide-react";
import { api, errMsg, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Auto-levels contrast + grayscale-weighted sharpening for better OCR legibility.
function enhance(ctx, w, h) {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  let min = 255;
  let max = 0;
  for (let i = 0; i < d.length; i += 16) {
    const l = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    if (l < min) min = l;
    if (l > max) max = l;
  }
  const range = Math.max(max - min, 1);
  for (let i = 0; i < d.length; i += 4) {
    for (let c = 0; c < 3; c += 1) d[i + c] = Math.max(0, Math.min(255, ((d[i + c] - min) * 255) / range));
  }
  ctx.putImageData(img, 0, 0);
}

function canvasToFile(canvas, name) {
  return new Promise((res) => canvas.toBlob((b) => res(new File([b], name, { type: "image/jpeg" })), "image/jpeg", 0.92));
}

async function processImage(source, sw, sh, crop, process) {
  const maxW = 1600;
  const cw = crop ? crop.w : sw;
  const ch = crop ? crop.h : sh;
  const scale = Math.min(1, maxW / cw);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(cw * scale);
  canvas.height = Math.round(ch * scale);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(source, crop ? crop.x : 0, crop ? crop.y : 0, cw, ch, 0, 0, canvas.width, canvas.height);
  if (process) enhance(ctx, canvas.width, canvas.height);
  return { file: await canvasToFile(canvas, "capture.jpg"), preview: canvas.toDataURL("image/jpeg", 0.8) };
}

export function CameraCapture({ facing = "environment", frame = "ktp", onCapture, testid, label }) {
  const video = useRef(null);
  const fileRef = useRef(null);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);
  const isKtp = frame === "ktp";

  useEffect(() => {
    let stream;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
      .then((s) => { stream = s; if (video.current) { video.current.srcObject = s; setReady(true); } })
      .catch(() => setErr("Kamera tidak dapat diakses. Izinkan kamera atau unggah dari galeri."));
    return () => stream && stream.getTracks().forEach((t) => t.stop());
  }, [facing]);

  const shoot = async () => {
    const v = video.current;
    const vw = v.videoWidth;
    const vh = v.videoHeight;
    const fw = vw * (isKtp ? 0.9 : 0.75);
    const fh = isKtp ? fw / 1.586 : fw * 1.25;
    const crop = { x: (vw - fw) / 2, y: Math.max(0, (vh - fh) / 2), w: fw, h: Math.min(fh, vh) };
    onCapture(await processImage(v, vw, vh, crop, isKtp));
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const img = new Image();
    img.onload = async () => onCapture(await processImage(img, img.width, img.height, null, isKtp));
    img.src = URL.createObjectURL(f);
    e.target.value = "";
  };

  return (
    <div data-testid={testid} className="space-y-3">
      <div className="relative rounded-3xl overflow-hidden bg-slate-900 aspect-[3/4]">
        {!err && <video ref={video} autoPlay playsInline muted className={`absolute inset-0 w-full h-full object-cover ${facing === "user" ? "scale-x-[-1]" : ""}`} />}
        {err && <p className="absolute inset-0 grid place-items-center text-center text-white/80 text-sm px-6">{err}</p>}
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className={`relative border-[3px] border-white/90 shadow-[0_0_0_9999px_rgba(2,6,23,0.55)] ${isKtp ? "w-[90%] aspect-[1.586] rounded-2xl" : "w-[75%] aspect-[4/5] rounded-[45%]"}`}>
            {isKtp && <span className="absolute inset-x-0 top-0 h-0.5 bg-emerald-400 shadow-[0_0_12px_#34d399] animate-[scan_2s_ease-in-out_infinite]" />}
          </div>
        </div>
        <p className="absolute top-3 inset-x-0 text-center text-white text-xs font-bold">{label}</p>
      </div>
      <div className="flex gap-2">
        <button type="button" data-testid={`${testid}-gallery`} onClick={() => fileRef.current?.click()} className="h-12 px-4 rounded-2xl border-2 border-slate-200 font-bold text-sm flex items-center gap-2"><ImagePlus className="w-4 h-4" />Galeri</button>
        <button type="button" data-testid={`${testid}-shoot`} disabled={!ready} onClick={shoot} className="flex-1 h-12 rounded-2xl bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"><Camera className="w-5 h-5" />Ambil Foto</button>
        <input ref={fileRef} type="file" accept="image/*" capture={facing} hidden onChange={pickFile} data-testid={`${testid}-file-input`} />
      </div>
    </div>
  );
}

const FIELDS = [
  ["nik", "NIK"], ["nama", "Nama"], ["tempat_lahir", "Tempat Lahir"], ["tanggal_lahir", "Tanggal Lahir", "date"],
  ["jenis_kelamin", "Jenis Kelamin"], ["alamat", "Alamat"], ["rt_rw", "RT/RW"], ["kel_desa", "Kel/Desa"],
  ["kecamatan", "Kecamatan"], ["kabupaten_kota", "Kabupaten/Kota"], ["provinsi", "Provinsi"], ["agama", "Agama"],
  ["status_perkawinan", "Status Perkawinan"], ["pekerjaan", "Pekerjaan"], ["kewarganegaraan", "Kewarganegaraan"],
];

function OcrResult({ data, setData, checks }) {
  return (
    <div className="space-y-4">
      <div data-testid="ocr-checks" className="bg-white rounded-2xl border p-4 space-y-1.5">
        <p className="font-display font-bold text-sm mb-1">Validasi otomatis</p>
        {checks.map((c) => (
          <p key={c.key} data-testid={`ocr-check-${c.key}`} className={`text-xs flex items-center gap-2 ${c.ok ? "text-emerald-700" : "text-red-600"}`}>
            {c.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}{c.label}
          </p>
        ))}
      </div>
      <div data-testid="ocr-fields" className="bg-white rounded-2xl border p-4 grid grid-cols-2 gap-3">
        {FIELDS.map(([k, l, type]) => (
          <label key={k} className={`block ${["nik", "nama", "alamat"].includes(k) ? "col-span-2" : ""}`}>
            <span className="text-[11px] font-semibold text-slate-500">{l}</span>
            <input data-testid={`ocr-field-${k}`} type={type || "text"} className={`field mt-0.5 h-10 ${k === "nik" ? "font-mono" : ""}`}
              value={data[k] || ""} onChange={(e) => setData({ ...data, [k]: k === "nik" ? e.target.value.replace(/\D/g, "").slice(0, 16) : e.target.value })} />
          </label>
        ))}
      </div>
      <p className="text-[11px] text-slate-500">Data terbaca otomatis dari KTP. Jika Anda mengubah NIK, nama, atau tanggal lahir, pengajuan akan diperiksa manual oleh admin.</p>
    </div>
  );
}

export function KycWizard() {
  const { refreshUser } = useAuth();
  const [step, setStep] = useState("ktp");
  const [ktp, setKtp] = useState(null);
  const [ocr, setOcr] = useState(null);
  const [data, setData] = useState({});
  const [selfie, setSelfie] = useState(null);
  const [busy, setBusy] = useState(false);

  const onKtp = async ({ file, preview }) => {
    setStep("scan");
    setKtp({ preview });
    try {
      const path = await uploadFile(file, "kyc");
      const { data: r } = await api.post("/kyc/ocr", { ktp_photo: path });
      setKtp({ preview, path });
      setOcr(r);
      setData(r.data);
      setStep("review");
    } catch (e) {
      toast.error(errMsg(e));
      setStep("ktp");
    }
  };
  const onSelfie = async ({ file, preview }) => {
    setBusy(true);
    try {
      const path = await uploadFile(file, "kyc");
      const { data: chk } = await api.post("/kyc/selfie-check", { selfie_photo: path });
      if (!chk.ok) {
        toast.error(`Selfie ditolak: ${chk.reason}. Silakan foto ulang.`);
        return;
      }
      toast.success("Selfie jelas & valid");
      setSelfie({ preview, path });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/kyc", { nik: data.nik, full_name: data.nama, birth_date: data.tanggal_lahir,
        ktp_photo: ktp.path, selfie_photo: selfie.path, details: data });
      toast.success(r.auto_verified ? "Identitas tervalidasi otomatis!" : "Data disimpan, menunggu verifikasi admin");
      await refreshUser();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (step === "ktp") return <><Steps n={1} /><CameraCapture testid="ktp-camera" label="Posisikan KTP di dalam bingkai" onCapture={onKtp} /></>;
  if (step === "scan") {
    return (
      <div data-testid="ocr-processing" className="space-y-4"><Steps n={1} />
        <div className="relative rounded-2xl overflow-hidden"><img src={ktp.preview} alt="KTP" className="w-full" />
          <div className="absolute inset-0 bg-emerald-900/40 grid place-items-center text-white"><div className="flex flex-col items-center gap-2"><ScanLine className="w-10 h-10 animate-pulse" /><p className="text-sm font-bold">Memproses gambar & membaca KTP (OCR)...</p></div></div>
        </div>
      </div>
    );
  }
  if (step === "review") {
    return (
      <div className="space-y-4"><Steps n={2} />
        <div className="relative"><img src={ktp.preview} alt="KTP" className="w-full rounded-2xl" />
          <button data-testid="retake-ktp-btn" onClick={() => setStep("ktp")} className="absolute top-2 right-2 h-8 px-3 rounded-full bg-white/90 text-xs font-bold flex items-center gap-1"><RefreshCw className="w-3 h-3" />Foto ulang</button></div>
        <OcrResult data={data} setData={setData} checks={ocr.checks} />
        <button data-testid="ocr-continue-btn" onClick={() => setStep("selfie")} disabled={!data.nik || data.nik.length !== 16 || !data.nama || !data.tanggal_lahir}
          className="w-full h-12 rounded-full bg-emerald-600 text-white font-bold disabled:opacity-50">Lanjut Foto Selfie</button>
      </div>
    );
  }
  return (
    <div className="space-y-4"><Steps n={3} />
      {selfie ? (
        <div className="space-y-3"><img src={selfie.preview} alt="Selfie" className="w-full rounded-3xl" />
          <button data-testid="retake-selfie-btn" onClick={() => setSelfie(null)} className="text-sm font-bold text-slate-600 flex items-center gap-1"><RefreshCw className="w-4 h-4" />Foto ulang</button></div>
      ) : busy ? (
        <div data-testid="selfie-checking" className="aspect-[3/4] rounded-3xl bg-slate-900 text-white grid place-items-center"><div className="flex flex-col items-center gap-2"><Loader2 className="w-8 h-8 animate-spin" /><p className="text-sm font-bold">Memeriksa kejelasan wajah...</p></div></div>
      ) : <CameraCapture testid="selfie-camera" facing="user" frame="face" label="Posisikan wajah Anda di dalam bingkai" onCapture={onSelfie} />}
      <button data-testid="submit-kyc-btn" onClick={submit} disabled={!selfie?.path || busy}
        className="w-full h-12 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}Simpan & Verifikasi
      </button>
    </div>
  );
}

function Steps({ n }) {
  return (
    <div className="flex items-center gap-2 mb-4" data-testid="kyc-steps">
      {["Foto KTP", "Cek Data", "Selfie"].map((s, i) => (
        <div key={s} className="flex-1"><div className={`h-1.5 rounded-full ${i < n ? "bg-emerald-500" : "bg-slate-200"}`} /><p className={`text-[11px] font-bold mt-1 ${i < n ? "text-emerald-700" : "text-slate-400"}`}>{s}</p></div>
      ))}
    </div>
  );
}
